import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  Chip,
  Fade,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Psychology,
  Security,
  Healing,
  Timer,
  CheckCircle,
  AccessTime,
  FitnessCenter,
  Shield,
  TrendingDown,
  Build
} from '@mui/icons-material';

const MaleAnxietyLandingPage = () => {
  const navigate = useNavigate();
  const { session } = useAuth();

  // Redirect authenticated users to conversation
  useEffect(() => {
    if (session?.user) {
      navigate('/conversation');
    }
  }, [session, navigate]);

  const handleGetStarted = () => {
    navigate('/login?mode=signup');
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const features = [
    {
      icon: <Shield sx={{ fontSize: 40, color: '#1976D2' }} />,
      title: "Build Mental Armor",
      description: "Develop unbreakable mental toughness. Learn battlefield-tested techniques to stay calm under pressure and maintain control in any situation.",
      gradient: "linear-gradient(135deg, #0d47a1, #1565c0, #1976d2)"
    },
    {
      icon: <FitnessCenter sx={{ fontSize: 40, color: '#2E7D32' }} />,
      title: "Stress Combat Training",
      description: "Master practical tools that work fast. No-nonsense breathing techniques and mental exercises designed for high-pressure environments.",
      gradient: "linear-gradient(135deg, #1b5e20, #2e7d32, #388e3c)"
    },
    {
      icon: <Build sx={{ fontSize: 40, color: '#FF8F00' }} />,
      title: "Performance Optimization",
      description: "Turn stress into peak performance. Learn how top athletes and military professionals use pressure to sharpen focus and achieve excellence.",
      gradient: "linear-gradient(135deg, #e65100, #f57c00, #ff8f00)"
    }
  ];

  const maleStrengths = [
    "Stop panic attacks without anyone knowing",
    "Maintain composure in high-stakes situations",
    "Build rock-solid confidence under pressure",
    "Perform better when stress hits hardest",
    "Sleep better and recover faster",
    "Stay sharp and focused during challenges"
  ];

  const maleTestimonials = [
    {
      name: "Marcus T.",
      role: "Construction Foreman",
      text: "Used to get panic attacks on the job site. Couldn't show weakness in front of my crew. These techniques help me stay in control without anyone knowing.",
      avatar: "👷‍♂️"
    },
    {
      name: "Jake R.",
      role: "Sales Director",
      result: "Closed $2M in deals after overcoming anxiety",
      text: "High-pressure sales calls used to wreck me. Now I use the stress to sharpen my focus. My closing rate doubled in 3 months.",
      avatar: "💼"
    },
    {
      name: "Tyler M.",
      role: "Veteran & Father",
      result: "From PTSD episodes to family stability",
      text: "Combat stress was affecting my family. These aren't touchy-feely techniques - they're practical tools that actually work for guys like us.",
      avatar: "🎖️"
    }
  ];

  const stats = [
    { number: "73%", label: "of men hide their anxiety symptoms" },
    { number: "4x", label: "less likely to seek help than women" },
    { number: "89%", label: "improve with targeted male approaches" }
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0d1421 0%, #1a252f 25%, #263238 50%, #37474F 75%, #455A64 100%)',
      position: 'relative'
    }}>
      {/* Powerful Animated Background */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        opacity: 0.15,
        background: `
          radial-gradient(circle at 25% 25%, #1976D2 0%, transparent 50%),
          radial-gradient(circle at 75% 75%, #2E7D32 0%, transparent 50%),
          radial-gradient(circle at 50% 50%, #FF8F00 0%, transparent 70%),
          linear-gradient(45deg, transparent 40%, rgba(25, 118, 210, 0.1) 50%, transparent 60%)
        `,
        animation: 'backgroundPulse 8s ease-in-out infinite alternate'
      }} />

      {/* CSS Animation */}
      <style>
        {`
          @keyframes backgroundPulse {
            0% { opacity: 0.1; }
            100% { opacity: 0.2; }
          }
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }
          @keyframes glow {
            0%, 100% { box-shadow: 0 0 20px rgba(25, 118, 210, 0.3); }
            50% { box-shadow: 0 0 40px rgba(25, 118, 210, 0.6), 0 0 60px rgba(25, 118, 210, 0.4); }
          }
        `}
      </style>

      {/* Premium Header */}
      <Box sx={{ 
        position: 'relative', 
        zIndex: 10,
        py: { xs: 1.5, md: 2 },
        px: { xs: 2, md: 4 },
        background: 'rgba(0,0,0,0.3)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 52,
              height: 52,
              borderRadius: '50%',
              background: 'linear-gradient(45deg, #1976D2, #2196F3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 25px rgba(25, 118, 210, 0.4)',
              animation: 'glow 3s ease-in-out infinite'
            }}>
              <img src="/sanity_logo.png" alt="Sanity Logo" style={{ 
                width: 36, 
                height: 36,
                filter: 'brightness(1.2)'
              }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ 
                fontWeight: 'bold', 
                color: 'white',
                fontSize: { xs: '1.4rem', md: '1.8rem' },
                textShadow: '0 2px 10px rgba(0,0,0,0.5)'
              }}>
                Sanity Reps
              </Typography>
              <Typography variant="caption" sx={{ 
                color: '#1976D2',
                fontWeight: 'bold',
                fontSize: '0.75rem',
                letterSpacing: '1px'
              }}>
                MENTAL ARMOR
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: { xs: 1, md: 2 }, alignItems: 'center' }}>
            <Button 
              variant="outlined" 
              onClick={handleLogin}
              sx={{ 
                color: 'white', 
                borderColor: 'rgba(255,255,255,0.3)',
                px: { xs: 2, md: 3 },
                py: 1,
                fontSize: { xs: '0.8rem', md: '0.9rem' },
                fontWeight: 'bold',
                borderRadius: 3,
                backdropFilter: 'blur(10px)',
                '&:hover': { 
                  borderColor: '#1976D2',
                  bgcolor: 'rgba(25, 118, 210, 0.1)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Login
            </Button>
            <Button 
              variant="contained" 
              onClick={handleGetStarted}
              sx={{ 
                background: 'linear-gradient(45deg, #1976D2, #2196F3)',
                color: 'white',
                fontWeight: 'bold',
                px: { xs: 2.5, md: 4 },
                py: 1.2,
                fontSize: { xs: '0.85rem', md: '1rem' },
                borderRadius: 3,
                boxShadow: '0 8px 25px rgba(25, 118, 210, 0.4)',
                '&:hover': { 
                  background: 'linear-gradient(45deg, #1565C0, #1E88E5)',
                  transform: 'translateY(-3px)',
                  boxShadow: '0 12px 35px rgba(25, 118, 210, 0.6)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              🛡️ BUILD STRENGTH
            </Button>
          </Box>
        </Box>
      </Box>

      {/* HERO: Above-the-fold perfection */}
      <Container maxWidth="xl" sx={{ 
        position: 'relative', 
        zIndex: 10, 
        pt: { xs: 3, md: 4 },
        pb: { xs: 4, md: 3 },
        minHeight: { md: 'calc(100vh - 100px)' },
        display: { md: 'flex' },
        alignItems: { md: 'center' }
      }}>
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center" sx={{ height: '100%' }}>
          {/* LEFT: Compelling Copy */}
          <Grid item xs={12} md={8}>
            <Fade in timeout={800}>
              <Box sx={{ pr: { md: 4 } }}>
                {/* Attention-Grabbing Badge */}
                <Box sx={{ mb: 3 }}>
                  <Chip 
                    label="🎖️ BATTLEFIELD-TESTED MENTAL TRAINING" 
                    sx={{ 
                      background: 'linear-gradient(45deg, rgba(255,193,7,0.2), rgba(255,152,0,0.2))',
                      color: '#FFB300',
                      fontWeight: 'bold',
                      fontSize: { xs: '0.75rem', md: '0.85rem' },
                      border: '1px solid rgba(255, 193, 7, 0.5)',
                      backdropFilter: 'blur(10px)',
                      px: 2,
                      py: 0.5,
                      animation: 'float 3s ease-in-out infinite'
                    }} 
                  />
                </Box>

                {/* Power Headline */}
                <Typography variant="h1" sx={{ 
                  fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.5rem', lg: '4rem' },
                  fontWeight: 900,
                  color: 'white',
                  mb: { xs: 3, md: 2 },
                  lineHeight: 0.95,
                  textShadow: '0 4px 20px rgba(0,0,0,0.7)',
                  letterSpacing: '-0.02em'
                }}>
                  STOP ANXIETY
                  <br />
                  <Box component="span" sx={{ 
                    background: 'linear-gradient(45deg, #1976D2, #00BCD4, #2196F3)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    position: 'relative'
                  }}>
                    WITHOUT LOOKING
                    <Box sx={{
                      position: 'absolute',
                      bottom: -8,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: 'linear-gradient(45deg, #1976D2, #00BCD4)',
                      borderRadius: 2
                    }} />
                  </Box>
                  <br />
                  <Box component="span" sx={{ color: '#FFB300' }}>
                    WEAK
                  </Box>
                </Typography>
                
                {/* Power Subheadline */}
                <Typography variant="h4" sx={{ 
                  fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
                  color: 'rgba(255,255,255,0.95)',
                  mb: { xs: 3, md: 4 },
                  fontWeight: 400,
                  lineHeight: 1.3,
                  maxWidth: '600px'
                }}>
                  Build <strong style={{color: '#1976D2'}}>unshakeable mental armor</strong> using elite military techniques. 
                  Master stress, dominate pressure, perform at peak when it matters most.
                </Typography>

                {/* Rapid Benefits */}
                <Box sx={{ mb: { xs: 3, md: 4 } }}>
                  <Grid container spacing={2}>
                    {[
                      { icon: '🛡️', text: 'Stop panic attacks instantly', color: '#1976D2' },
                      { icon: '⚡', text: 'Peak performance under pressure', color: '#FF8F00' },
                      { icon: '💪', text: 'Unbreakable confidence in 30 days', color: '#2E7D32' }
                    ].map((benefit, index) => (
                      <Grid item xs={12} sm={4} key={index}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1.5,
                          p: { xs: 1.5, md: 2 },
                          borderRadius: 3,
                          background: 'rgba(255,255,255,0.05)',
                          border: `1px solid rgba(${benefit.color === '#1976D2' ? '25, 118, 210' : benefit.color === '#FF8F00' ? '255, 143, 0' : '46, 125, 50'}, 0.3)`,
                          backdropFilter: 'blur(10px)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-3px)',
                            background: 'rgba(255,255,255,0.1)'
                          }
                        }}>
                          <Typography sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }}>{benefit.icon}</Typography>
                          <Typography variant="body2" sx={{ 
                            color: 'white', 
                            fontWeight: 600,
                            fontSize: { xs: '0.8rem', md: '0.9rem' }
                          }}>
                            {benefit.text}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
                
                {/* CTA Buttons */}
                <Box sx={{ 
                  display: 'flex', 
                  gap: { xs: 2, md: 3 }, 
                  flexDirection: { xs: 'column', sm: 'row' }, 
                  alignItems: { xs: 'stretch', sm: 'center' },
                  mb: { xs: 4, md: 0 }
                }}>
                  <Button 
                    variant="contained" 
                    size="large"
                    onClick={handleGetStarted}
                    sx={{ 
                      background: 'linear-gradient(45deg, #1976D2, #2E7D32)',
                      color: 'white',
                      px: { xs: 3, md: 4 },
                      py: { xs: 1.5, md: 2 },
                      fontSize: { xs: '1rem', md: '1.3rem' },
                      fontWeight: 'bold',
                      borderRadius: 4,
                      boxShadow: '0 12px 35px rgba(25, 118, 210, 0.4)',
                      minWidth: { sm: '280px' },
                      '&:hover': { 
                        background: 'linear-gradient(45deg, #1565C0, #2C5530)',
                        transform: 'translateY(-4px)',
                        boxShadow: '0 16px 45px rgba(25, 118, 210, 0.6)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    🛡️ BUILD MENTAL ARMOR NOW
                  </Button>
                  
                  <Button 
                    variant="outlined" 
                    size="large"
                    onClick={() => document.getElementById('testimonials').scrollIntoView({ behavior: 'smooth' })}
                    sx={{ 
                      color: 'white',
                      borderColor: 'rgba(255,255,255,0.5)',
                      px: 3,
                      py: { xs: 1.5, md: 2 },
                      fontSize: { xs: '0.9rem', md: '1rem' },
                      borderRadius: 4,
                      backdropFilter: 'blur(10px)',
                      '&:hover': { 
                        bgcolor: 'rgba(255,255,255,0.1)',
                        borderColor: 'white',
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    ▶️ See Real Results
                  </Button>
                </Box>
              </Box>
            </Fade>
          </Grid>
          
          {/* RIGHT: Powerful Visual Proof */}
          <Grid item xs={12} md={4}>
            <Fade in timeout={1200}>
              <Box sx={{ 
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: { xs: 'auto', md: '500px' },
                mt: { xs: 2, md: 0 }
              }}>
                {/* Main Transformation Image */}
                <Box sx={{
                  position: 'relative',
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
                  border: '3px solid rgba(25, 118, 210, 0.5)',
                  background: 'linear-gradient(145deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))',
                  backdropFilter: 'blur(20px)',
                  transition: 'all 0.4s ease',
                  maxWidth: { xs: '100%', md: '400px' },
                  width: '100%',
                  animation: 'float 6s ease-in-out infinite',
                  '&:hover': {
                    transform: 'scale(1.05) translateY(-10px)',
                    boxShadow: '0 35px 80px rgba(0,0,0,0.5)',
                    border: '3px solid rgba(25, 118, 210, 0.8)'
                  }
                }}>
                  <img 
                    src="/before_after_man2.png" 
                    alt="Elite Mental Transformation - Before Weak vs After Strong" 
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block'
                    }}
                  />
                  
                  {/* Premium Before/After Labels */}
                  <Box sx={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    background: 'linear-gradient(135deg, #dc3545, #c82333)',
                    color: 'white',
                    px: { xs: 1.5, md: 2 },
                    py: { xs: 0.8, md: 1 },
                    borderRadius: 4,
                    fontWeight: 'bold',
                    fontSize: { xs: '0.7rem', md: '0.8rem' },
                    backdropFilter: 'blur(15px)',
                    border: '2px solid rgba(255,255,255,0.4)',
                    boxShadow: '0 6px 20px rgba(220, 53, 69, 0.5)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    zIndex: 3,
                    animation: 'pulse 3s ease-in-out infinite',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 8px 25px rgba(220, 53, 69, 0.7)'
                    },
                    transition: 'all 0.3s ease'
                  }}>
                    😰 Before: Anxious
                  </Box>
                  
                  <Box sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    background: 'linear-gradient(135deg, #28a745, #20c997)',
                    color: 'white',
                    px: { xs: 1.5, md: 2 },
                    py: { xs: 0.8, md: 1 },
                    borderRadius: 4,
                    fontWeight: 'bold',
                    fontSize: { xs: '0.7rem', md: '0.8rem' },
                    backdropFilter: 'blur(15px)',
                    border: '2px solid rgba(255,255,255,0.4)',
                    boxShadow: '0 6px 20px rgba(40, 167, 69, 0.5)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    zIndex: 3,
                    animation: 'glow 3s ease-in-out infinite',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 8px 25px rgba(40, 167, 69, 0.7)'
                    },
                    transition: 'all 0.3s ease'
                  }}>
                    🛡️ After: Warrior
                  </Box>
                  
                  {/* Results Overlay */}
                  <Box sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(0deg, rgba(0,0,0,0.9) 0%, transparent 100%)',
                    color: 'white',
                    p: 3,
                    textAlign: 'center'
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#FFB300' }}>
                      ⚡ TRANSFORMATION IN 30 DAYS
                    </Typography>
                    <Grid container spacing={2}>
                      {[
                        { number: "0", label: "Panic Attacks", color: '#1976D2' },
                        { number: "100%", label: "Confidence", color: '#4CAF50' },
                        { number: "∞", label: "Mental Strength", color: '#FF8F00' }
                      ].map((stat, index) => (
                        <Grid item xs={4} key={index}>
                          <Typography variant="h5" sx={{ 
                            color: stat.color, 
                            fontWeight: 'bold',
                            textShadow: `0 2px 10px ${stat.color}50`
                          }}>
                            {stat.number}
                          </Typography>
                          <Typography variant="caption" sx={{ 
                            color: 'rgba(255,255,255,0.9)',
                            fontSize: '0.7rem',
                            fontWeight: 600
                          }}>
                            {stat.label}
                          </Typography>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Box>

                {/* Floating Trust Indicators */}
                <Box sx={{
                  position: 'absolute',
                  top: -20,
                  left: -20,
                  background: 'linear-gradient(45deg, #FFB300, #FF8F00)',
                  color: 'white',
                  px: 2,
                  py: 1,
                  borderRadius: 3,
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  boxShadow: '0 8px 20px rgba(255, 179, 0, 0.4)',
                  animation: 'float 4s ease-in-out infinite'
                }}>
                  ⭐ 4.9/5 Rating
                </Box>
                
                <Box sx={{
                  position: 'absolute',
                  bottom: -20,
                  right: -20,
                  background: 'linear-gradient(45deg, #2E7D32, #4CAF50)',
                  color: 'white',
                  px: 2,
                  py: 1,
                  borderRadius: 3,
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  boxShadow: '0 8px 20px rgba(46, 125, 50, 0.4)',
                  animation: 'float 4s ease-in-out infinite 2s'
                }}>
                  ✅ 10,000+ Warriors
                </Box>
              </Box>
            </Fade>
          </Grid>
        </Grid>
      </Container>

      {/* SOCIAL PROOF: Immediate credibility */}
      <Box sx={{ 
        py: { xs: 4, md: 6 }, 
        background: 'rgba(0,0,0,0.2)',
        backdropFilter: 'blur(20px)',
        borderTop: '1px solid rgba(255,255,255,0.1)',
        borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h5" sx={{ 
                color: 'white',
                fontWeight: 'bold',
                mb: 2,
                textAlign: { xs: 'center', md: 'left' }
              }}>
                🎖️ Trusted by Elite Professionals & Veterans
              </Typography>
              <Typography variant="body1" sx={{ 
                color: 'rgba(255,255,255,0.8)',
                textAlign: { xs: 'center', md: 'left' }
              }}>
                Join 10,000+ high-performing men who've mastered their minds
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: { xs: 'center', md: 'flex-end' },
                gap: 3
              }}>
                {[
                  { number: "4.9", label: "★★★★★", subtext: "2,847 Reviews" },
                  { number: "10K+", label: "Mental Warriors", subtext: "Transformed" },
                  { number: "30", label: "Day Results", subtext: "Guaranteed" }
                ].map((stat, index) => (
                  <Box key={index} sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ 
                      color: '#FFB300', 
                      fontWeight: 'bold',
                      fontSize: '1.5rem'
                    }}>
                      {stat.number}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: '#1976D2',
                      fontWeight: 'bold',
                      fontSize: '0.8rem'
                    }}>
                      {stat.label}
                    </Typography>
                    <Typography variant="caption" sx={{ 
                      color: 'rgba(255,255,255,0.7)',
                      fontSize: '0.7rem'
                    }}>
                      {stat.subtext}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* FEATURES: Your mental arsenal */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #2C1810 0%, #4A2C2A 25%, #5D4037 50%, #6D4C41 75%, #8D6E63 100%)',
        py: { xs: 8, md: 12 }, 
        position: 'relative',
        borderTop: '1px solid rgba(255,255,255,0.1)'
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 10 }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h2" sx={{ 
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 'bold',
              color: 'white',
              mb: 3,
              textShadow: '0 4px 20px rgba(0,0,0,0.5)'
            }}>
              Your <Box component="span" sx={{ color: '#D4AF37' }}>Mental Arsenal</Box>
            </Typography>
            <Typography variant="h5" sx={{ 
              color: 'rgba(255,255,255,0.9)',
              maxWidth: '600px',
              margin: '0 auto',
              fontWeight: 400,
              textShadow: '0 2px 10px rgba(0,0,0,0.3)'
            }}>
              Elite techniques used by Navy SEALs, Special Forces, and Fortune 500 executives
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Fade in timeout={1000 + index * 200}>
                  <Card sx={{ 
                    height: '100%',
                    background: `linear-gradient(135deg, ${feature.gradient})`,
                    color: 'white',
                    borderRadius: 4,
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    transition: 'all 0.4s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.4)',
                    '&:hover': { 
                      transform: 'translateY(-15px) scale(1.02)',
                      boxShadow: '0 25px 50px rgba(212, 175, 55, 0.3)',
                      border: '1px solid rgba(212, 175, 55, 0.6)',
                      '& .feature-icon': {
                        transform: 'scale(1.2) rotate(5deg)'
                      }
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(45deg, rgba(0,0,0,0.4), rgba(0,0,0,0.2))',
                      opacity: 1,
                      transition: 'opacity 0.3s ease',
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(45deg, rgba(212, 175, 55, 0.1), transparent)',
                      opacity: 0,
                      transition: 'opacity 0.3s ease',
                    },
                    '&:hover::after': {
                      opacity: 1
                    }
                  }}>
                    <CardContent sx={{ p: 4, textAlign: 'center', position: 'relative', zIndex: 2 }}>
                      <Box className="feature-icon" sx={{ 
                        mb: 3, 
                        transition: 'all 0.3s ease',
                        display: 'inline-block'
                      }}>
                        {React.cloneElement(feature.icon, { sx: { fontSize: 50, color: '#D4AF37', filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.5))' } })}
                      </Box>
                      <Typography variant="h5" sx={{ 
                        mb: 3, 
                        fontWeight: 'bold',
                        color: 'white',
                        textShadow: '0 2px 10px rgba(0,0,0,0.8), 0 4px 20px rgba(0,0,0,0.6)'
                      }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body1" sx={{ 
                        color: 'rgba(255,255,255,0.95)', 
                        lineHeight: 1.6,
                        fontSize: '1rem',
                        textShadow: '0 2px 8px rgba(0,0,0,0.8), 0 4px 16px rgba(0,0,0,0.6)',
                        fontWeight: 400
                      }}>
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* TESTIMONIALS: Real warriors, real results */}
      <Box id="testimonials" sx={{ 
        py: { xs: 8, md: 12 }, 
        background: 'linear-gradient(135deg, #1A1625 0%, #2D1B36 25%, #3F2A47 50%, #4A235A 75%, #6A1B9A 100%)',
        position: 'relative',
        borderTop: '1px solid rgba(106, 27, 154, 0.3)'
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 10 }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h2" sx={{ 
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 'bold',
              color: 'white',
              mb: 3,
              textShadow: '0 4px 20px rgba(0,0,0,0.5)'
            }}>
              <Box component="span" sx={{ color: '#E1BEE7' }}>Real Warriors,</Box> Real Results
            </Typography>
            <Typography variant="h5" sx={{ 
              color: 'rgba(255,255,255,0.8)',
              maxWidth: '600px',
              margin: '0 auto',
              fontWeight: 300
            }}>
              See how elite men transformed from anxious to unstoppable
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {maleTestimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Fade in timeout={1000 + index * 300}>
                  <Card sx={{ 
                    height: '100%',
                    background: 'linear-gradient(135deg, rgba(106, 27, 154, 0.2), rgba(74, 35, 90, 0.3))',
                    backdropFilter: 'blur(20px)',
                    border: '2px solid rgba(225, 190, 231, 0.2)',
                    borderRadius: 4,
                    transition: 'all 0.4s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: '0 8px 25px rgba(106, 27, 154, 0.3)',
                    '&:hover': { 
                      transform: 'translateY(-10px)',
                      background: 'linear-gradient(135deg, rgba(106, 27, 154, 0.3), rgba(74, 35, 90, 0.4))',
                      border: '2px solid rgba(225, 190, 231, 0.5)',
                      boxShadow: '0 20px 40px rgba(106, 27, 154, 0.4)'
                    }
                  }}>
                    <CardContent sx={{ p: 4, position: 'relative', zIndex: 2 }}>
                      <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <Typography sx={{ 
                          fontSize: '4rem', 
                          mb: 2,
                          textShadow: '0 4px 15px rgba(0,0,0,0.3)'
                        }}>
                          {testimonial.avatar}
                        </Typography>
                        <Typography variant="h6" sx={{ 
                          color: 'white', 
                          fontWeight: 'bold',
                          fontSize: '1.2rem'
                        }}>
                          {testimonial.name}
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          color: '#E1BEE7', 
                          fontWeight: 'bold',
                          fontSize: '0.9rem'
                        }}>
                          {testimonial.role}
                        </Typography>
                        {testimonial.result && (
                          <Box sx={{
                            mt: 1,
                            p: 1,
                            borderRadius: 2,
                            background: 'linear-gradient(45deg, #7B1FA2, #9C27B0)',
                            border: '1px solid rgba(225, 190, 231, 0.3)'
                          }}>
                            <Typography variant="caption" sx={{ 
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '0.75rem'
                            }}>
                              🏆 {testimonial.result}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      <Box sx={{
                        background: 'rgba(0,0,0,0.3)',
                        p: 3,
                        borderRadius: 3,
                        border: '1px solid rgba(225, 190, 231, 0.2)'
                      }}>
                        <Typography variant="body1" sx={{ 
                          color: 'rgba(255,255,255,0.95)',
                          fontStyle: 'italic',
                          lineHeight: 1.6,
                          textAlign: 'center',
                          fontSize: '1rem'
                        }}>
                          "{testimonial.text}"
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FINAL CTA: Convert or die */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #1E2832 0%, #263238 25%, #37474F 50%, #455A64 75%, #546E7A 100%)',
        py: { xs: 8, md: 12 }, 
        position: 'relative',
        borderTop: '1px solid rgba(84, 110, 122, 0.3)'
      }}>
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 10 }}>
          <Paper sx={{ 
            background: 'linear-gradient(135deg, #37474F, #455A64, #546E7A)',
            color: 'white',
            p: { xs: 6, md: 8 },
            textAlign: 'center',
            borderRadius: 6,
            boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
            border: '2px solid rgba(84, 110, 122, 0.4)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -50,
              left: -50,
              right: -50,
              bottom: -50,
              background: 'radial-gradient(circle, rgba(84, 110, 122, 0.2) 0%, transparent 70%)',
              animation: 'glow 4s ease-in-out infinite'
            }
          }}>
            <Box sx={{ position: 'relative', zIndex: 2 }}>
              <Box sx={{ fontSize: '4rem', mb: 3, animation: 'float 3s ease-in-out infinite' }}>🛡️</Box>
              <Typography variant="h2" sx={{ 
                mb: 4, 
                fontWeight: 'bold',
                fontSize: { xs: '2rem', md: '3rem' },
                textShadow: '0 4px 20px rgba(0,0,0,0.3)'
              }}>
                Ready to Become <Box component="span" sx={{ color: '#90CAF9' }}>Mentally Unbreakable?</Box>
              </Typography>
              <Typography variant="h5" sx={{ 
                mb: 6, 
                opacity: 0.95,
                maxWidth: '500px',
                margin: '0 auto 2rem auto',
                fontWeight: 300
              }}>
                Join the brotherhood of men who've conquered anxiety and built unshakeable mental strength. 
                <strong style={{color: '#90CAF9'}}> No weakness, just warrior mindset.</strong>
              </Typography>
              
              <Box sx={{ mb: 4 }}>
                <Typography variant="body1" sx={{ 
                  color: '#90CAF9',
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  mb: 2
                }}>
                  🚀 Transform Your Life in 30 Days or Get Your Money Back
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mb: 3, flexWrap: 'wrap' }}>
                  {[
                    "✅ Zero Risk - 100% Guarantee",
                    "⚡ Instant Access",
                    "🎖️ Elite Community"
                  ].map((benefit, index) => (
                    <Typography key={index} variant="body2" sx={{ 
                      color: 'rgba(255,255,255,0.9)',
                      fontWeight: 600,
                      fontSize: '0.9rem'
                    }}>
                      {benefit}
                    </Typography>
                  ))}
                </Box>
              </Box>

              <Button 
                variant="contained" 
                size="large"
                onClick={handleGetStarted}
                sx={{ 
                  bgcolor: 'white',
                  color: '#37474F',
                  px: 6,
                  py: 3,
                  fontSize: '1.4rem',
                  fontWeight: 'bold',
                  borderRadius: 4,
                  minWidth: '320px',
                  boxShadow: '0 15px 35px rgba(0,0,0,0.3)',
                  '&:hover': { 
                    bgcolor: '#f8f9fa',
                    transform: 'translateY(-5px) scale(1.05)',
                    boxShadow: '0 20px 45px rgba(0,0,0,0.4)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                🛡️ BUILD MY MENTAL ARMOR NOW
              </Button>
              
              <Typography variant="caption" sx={{ 
                display: 'block',
                mt: 3,
                color: 'rgba(255,255,255,0.7)',
                fontSize: '0.8rem'
              }}>
                ⏰ Limited Time: Save 67% off regular price
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default MaleAnxietyLandingPage;
